/**
 * Judge.me Accessibility Improvements
 *
 * Improves screen reader and keyboard support for Judge.me ratings,
 * review badges and histogram filters.
 *
 * Main improvements:
 * - removes unnecessary interactive roles from decorative stars;
 * - hides individual star icons from screen readers;
 * - provides one readable rating announcement;
 * - moves focus to the updated review list after histogram filtering.
 */

(function () {
  let updateScheduled = false;

  /**
   * Moves focus into the updated review results after a Judge.me
   * histogram filter has been activated.
   */
  function moveFocusToUpdatedJudgeMeReviews() {
    const reviewsBody = document.querySelector(".jdgm-all-reviews__body");

    if (!reviewsBody) return;

    let focusMoved = false;

    function moveFocus() {
      if (focusMoved) return;

      const focusable = Array.from(
        reviewsBody.querySelectorAll(
          [
            "a[href]",
            "button:not([disabled])",
            "input:not([disabled])",
            "select:not([disabled])",
            "textarea:not([disabled])",
            '[tabindex]:not([tabindex="-1"])',
          ].join(","),
        ),
      ).find(function (element) {
        return (
          window.ASA11y.isVisible(element) &&
          !element.closest(".jdgm-histogram")
        );
      });

      if (focusable) {
        focusMoved = true;
        focusable.focus();
        return;
      }

      /*
       * Fallback: if the review contains no interactive controls,
       * focus the first review itself programmatically.
       */
      const firstReview = reviewsBody.querySelector(".jdgm-rev");

      if (firstReview && window.ASA11y.isVisible(firstReview)) {
        focusMoved = true;
        firstReview.setAttribute("tabindex", "-1");
        firstReview.focus();
      }
    }

    const reviewsObserver = new MutationObserver(function () {
      requestAnimationFrame(function () {
        requestAnimationFrame(moveFocus);
      });
    });

    reviewsObserver.observe(reviewsBody, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    window.setTimeout(moveFocus, 300);
    window.setTimeout(moveFocus, 700);

    window.setTimeout(function () {
      reviewsObserver.disconnect();
    }, 1500);
  }

  /**
   * Allows Judge.me histogram filters to update the reviews first,
   * then moves focus into the newly filtered review content.
   */
  function fixJudgeMeHistogramFocusA11y() {
    document
      .querySelectorAll(".jdgm-histogram__star")
      .forEach(function (histogramStar) {
        if (histogramStar.dataset.a11yFocusFixed === "true") return;

        histogramStar.addEventListener("keydown", function (event) {
          if (event.key !== "Enter" && event.key !== " ") return;

          window.setTimeout(moveFocusToUpdatedJudgeMeReviews, 0);
        });

        histogramStar.dataset.a11yFocusFixed = "true";
      });
  }

  /**
   * Removes duplicate or incorrect roles from Judge.me rating widgets
   * and provides a single useful accessible rating label.
   */
  function fixJudgeMeRatings() {
    document
      .querySelectorAll(
        [
          ".jdgm-prev-badge__stars",
          ".jdgm-carousel-item__review-rating",
          ".jdgm-rev__rating",
        ].join(","),
      )
      .forEach(function (rating) {
        const score = rating.dataset.score || rating.getAttribute("data-score");

        rating.removeAttribute("role");
        rating.removeAttribute("tabindex");
        rating.removeAttribute("aria-pressed");

        if (score) {
          rating.setAttribute("aria-label", score + " out of 5 stars");
        }
      });

    /*
     * Individual stars are decorative because the rating is announced
     * once by the parent rating element.
     */
    document.querySelectorAll(".jdgm-star").forEach(function (star) {
      star.removeAttribute("role");
      star.removeAttribute("tabindex");
      star.removeAttribute("aria-label");
      star.setAttribute("aria-hidden", "true");
    });

    document
      .querySelectorAll(".jdgm-all-reviews__summary-stars[tabindex]")
      .forEach(function (summaryStars) {
        summaryStars.removeAttribute("tabindex");
      });

    document.querySelectorAll(".jdgm-prev-badge").forEach(function (badge) {
      const text = badge
        .querySelector(".jdgm-prev-badge__text")
        ?.textContent?.trim();

      const score = badge.querySelector(".jdgm-prev-badge__stars")?.dataset
        .score;

      if (score && text) {
        badge.setAttribute("aria-label", score + " out of 5 stars, " + text);
      }
    });

    fixJudgeMeHistogramFocusA11y();
  }

  /**
   * Replaces rating aria-labels with visually hidden readable text,
   * preventing screen readers from announcing each star separately.
   */
  function fixJudgeMeRatingLabels() {
    document
      .querySelectorAll(
        [
          ".jdgm-prev-badge__stars[aria-label]",
          ".jdgm-rev__rating[aria-label]",
        ].join(","),
      )
      .forEach(function (rating) {
        const label = rating.getAttribute("aria-label");

        if (!label) return;

        rating.removeAttribute("aria-label");
        rating.removeAttribute("role");
        rating.setAttribute("aria-hidden", "true");

        const parent = rating.parentElement;

        if (!parent) return;

        if (!parent.querySelector(".as-jdgm-rating-label")) {
          const screenReaderLabel = document.createElement("span");

          screenReaderLabel.className = "as-jdgm-rating-label m:hidden";
          screenReaderLabel.textContent = label;

          parent.insertBefore(screenReaderLabel, rating);
        }
      });
  }

  function runJudgeMeAccessibilityFixes() {
    fixJudgeMeRatings();
    fixJudgeMeRatingLabels();
  }

  function scheduleJudgeMeFixes() {
    if (updateScheduled) return;

    updateScheduled = true;

    const schedule = window.requestIdleCallback || window.requestAnimationFrame;

    schedule(function () {
      updateScheduled = false;
      runJudgeMeAccessibilityFixes();
    });
  }

  function initJudgeMeAccessibility() {
    runJudgeMeAccessibilityFixes();

    window.setTimeout(runJudgeMeAccessibilityFixes, 1000);
    window.setTimeout(runJudgeMeAccessibilityFixes, 2500);

    new MutationObserver(scheduleJudgeMeFixes).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-label", "class"],
    });
  }

  window.ASA11y.onReady(initJudgeMeAccessibility);
})();
